import * as angular from "angular";
import { copy } from "angular";
import { SettingsName } from "../class/SettingsName";
import { IAccountUserMapping } from "../interfaces/IUser";
import { ConstantsService } from "./constantsService";
import { SessionService } from "./session.service";
import { TranslationService } from "./translation.service";

export interface IAccountUserMappingUpdate extends IAccountUserMapping {
    Email: string
}

export class AccountUserMappingService {
    isDemoSite: boolean;

    static $inject = ["$q", "$http", "sessionService", "constantsService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly sessionService: SessionService,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService) {

        this.isDemoSite = this.constantsService.getIsDemoSite();
    }

    getAccountUserMappings(invalidateCache = false) {
        return this
            .$http({
                url: "/api/userAccountMappings",
                method: "GET",
                params: { minsToLive: 30, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IAccountUserMapping[]>) => this.formatResponse(response));
    }

    getAccountUserMappingsForAccount(accountId: number, invalidateCache = false) {
        return this
            .$http({
                url: `/api/userAccountMappings/${accountId}`,
                method: "GET",
                params: { minsToLive: 30, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IAccountUserMapping[]>) => this.formatResponse(response));
    }

    getCurrentAccountUserMappings(invalidateCache = false) {
        return this
            .$http({
                url: "/api/userAccountMappings/current",
                method: "GET",
                params: { minsToLive: 30, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IAccountUserMapping[]>) => this.formatResponse(response));
    }

    postAccountUserMapping(email: string, accountUserMapping: IAccountUserMapping, userLanguage: string): angular.IPromise<IAccountUserMapping> {
        const update = accountUserMapping as IAccountUserMappingUpdate

        update.Email = email;

        return this
            .$http({
                method: "POST",
                url: "/api/userAccountMappings/",
                data: update,
                params: { userLanguage: userLanguage ?? "en" }
            })
            .then((response: angular.IHttpResponse<IAccountUserMapping>) => {
                return response.data;
            });
    }

    hasCachedAccountUserMappings(): boolean {
        return this.sessionService.getItem<IAccountUserMapping[]>(SettingsName.AccountUserMappings)?.length > 0;
    }

    updateCachedAccountUserMapping(userId: number, replacementMapping: IAccountUserMapping): boolean {
        if (!this.hasCachedAccountUserMappings()) {
            return false;
        }

        const cachedAccountUserMappings: IAccountUserMapping[] = copy(this.sessionService.getItem(SettingsName.AccountUserMappings));
        const currentAccountUserMappingIndex = cachedAccountUserMappings.findIndex(mapping => mapping.UserId === userId);

        if (currentAccountUserMappingIndex === -1) {
            return false;
        }

        cachedAccountUserMappings[currentAccountUserMappingIndex] = replacementMapping;

        this.sessionService.setItem(SettingsName.AccountUserMappings, cachedAccountUserMappings);
    }

    private formatResponse(response: angular.IHttpResponse<IAccountUserMapping[]>) {
        const accountMappings = response.data

        if (this.isDemoSite) {
            accountMappings.forEach(mapping => {
                mapping.Name = this.translationService.t('currentUserService.demo');
            })
        }

        return accountMappings;
    }
}
